import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Simple windowed list for performance on large arrays.
 * Assumes approximately fixed item height.
 */
export default function WindowedList({
  items,
  renderItem,
  itemHeight = 256,
  overscan = 6,
  useWindowScroll = false,
  containerId = 'scroll-container',
  minColumnWidth = Infinity, // set e.g. 360 to allow two columns when width permits
  gap = 0,
}) {
  const [range, setRange] = useState({ startRow: 0, endRow: 10, columns: 1, containerWidth: 0 });
  const rafRef = useRef(null);

  const getContainer = useCallback(() => {
    if (useWindowScroll) return document.documentElement;
    return document.getElementById(containerId);
  }, [useWindowScroll, containerId]);

  const computeRange = useCallback(() => {
    const container = getContainer();
    if (!container) return;
    const scrollTop = container.scrollTop; // both cases return an element with scrollTop
    const viewportHeight = useWindowScroll ? window.innerHeight : container.clientHeight || window.innerHeight;
    const measureEl = document.getElementById(containerId) || container;
  const containerWidth = measureEl.clientWidth || window.innerWidth;
    const columns = Math.max(1, Math.floor((containerWidth + gap) / (minColumnWidth + gap)));

    const totalItems = items.length;
    const rowCount = Math.ceil(totalItems / columns);
    const firstVisibleRow = Math.floor(scrollTop / itemHeight);
    const visibleRowCount = Math.ceil(viewportHeight / itemHeight);
    const startRow = Math.max(0, firstVisibleRow - overscan);
    const endRow = Math.min(rowCount, firstVisibleRow + visibleRowCount + overscan);
  setRange({ startRow, endRow, columns, containerWidth });
  }, [getContainer, items.length, itemHeight, overscan, useWindowScroll, containerId, minColumnWidth, gap]);

  // Throttle scroll/resize with rAF
  const onScrollOrResize = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      computeRange();
    });
  }, [computeRange]);

  useEffect(() => {
    computeRange();
    const container = getContainer();
    if (!container) return;
    const target = useWindowScroll ? window : container;
    target.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [computeRange, useWindowScroll, getContainer, onScrollOrResize]);

  useEffect(() => {
    // Recompute when items change
    computeRange();
  }, [items, computeRange]);

  const columns = range.columns || 1;
  const rowCount = Math.ceil(items.length / columns);
  const totalHeight = rowCount * itemHeight;
  const startIndex = range.startRow * columns;
  const endIndex = Math.min(items.length, range.endRow * columns);
  const visibleItems = items.slice(startIndex, endIndex);
  const topOffset = range.startRow * itemHeight;
  const colGap = gap;
  const itemWidth = columns === 1
    ? '100%'
    : `calc((100% - ${(columns - 1) * colGap}px) / ${columns})`;

  return (
    <div style={{ position: 'relative', height: totalHeight }}>
      <div style={{ height: topOffset }} />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignContent: 'flex-start',
          rowGap: 0,
          columnGap: colGap,
        }}
      >
        {visibleItems.map((item, idx) => {
          const realIndex = startIndex + idx;
          return (
            <div
              key={item?.id ?? realIndex}
              style={{
                width: itemWidth,
                height: itemHeight,
                paddingTop: '4px',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {renderItem(item, realIndex)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
